import {
    Grid,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Modal,
    Button,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@material-ui/core';
import {observer} from 'mobx-react-lite';
import {useEffect, useState} from 'react';
import API from '../APIs/API';
import {makeStyles} from '@material-ui/core/styles';
import {ExpandMore} from '@material-ui/icons';
import dynamic from 'next/dynamic';

const ReactJson = dynamic(import('react-json-view'), {ssr: false});

type People = Map<string, Person>;
type Parties = Map<string, Party>;
type Holding = {Policy: Policy};
type OLifE = {Party: Party; Holding: Holding | Holding[]};
type Person = {
    Gender: {_: string};
    FirstName: string;
    LastName: string;
    role: string;
    Age: string;
    BirthDate: string;
    firstName: string;
    lastName: string;
    gender: string;
    age: string;
    dob: string;
};
type Policy = {
    ProductCode: string;
    PolNumber: string;
    PolicyStatus?: {_: string};
    AnnualPaymentAmt: string;
    PlanName: string;
    Life: {
        FaceAmt: string;
        Coverage: {
            LifeParticipant: {$: {PartyID: string} | null}[];
        };
    };
    Coverage: {
        LifeParticipant: {PartyID: string; LifeParticipantRoleCode: {_: string}}[];
    };
    CarrierCode: string;
    EffDate: string;
    PaidToDate: string;
    RequirementInfo: {
        $?: {id: string};
        ReqCode: {$: {tc: string}; _: string};
        ReqStatus: {$: {tc: string}; _: string};
        HORequirementRefID: string;
        RequestedDate: string;
        RequirementDetails: string;
        RequirementInfoUniqueID: string;
    }[];
};
type Party = {
    id: string;
    $?: {id: string};
    Person?: Person;
    firstName: string;
    lastName: string;
    gender: string;
    age: string;
    dob: string;
};
type HoldingData = {
    status: string;
    annualPaymentAmt: string;
    planName: string;
    faceAmount: string;
    carrierCode: string;
    effDate: string;
    paidToDate: string;
    holding: Holding;
    requirementInfo: {
        id: string | undefined;
        hoRequirementRefID: string;
        reqCodeTc: string;
        reqCode: string;
        reqStatusTc: string;
        reqStatus: string;
        requestedDate: string;
        requirementDetails: string;
        requirementInfoUniqueID: string;
    }[];
    people: (Person | null | undefined)[];
    transDate: Date | string;
};

export const DataFeedPanel = observer(() => {
    const feedType = {
        pending: '1125-Pending-Business-Production',
        inforce: '1203-Inforce-Policy-Production',
    };
    const participantRoleType = {
        owner: 'Owner',
        primaryInsured: 'Primary Insured',
        beneficiaryPrimary: 'Beneficiary - Primary',
    };
    function getModalStyle() {
        return {
            top: '50%',
            left: '50%',
            transform: `translate(-50%, -50%)`,
        };
    }
    const [carrier, setCarrier] = useState('National_Life');
    const [feed, setFeed] = useState(feedType.pending);
    const [data, setData] = useState(new Array<HoldingData | undefined>());
    const [modalState, setModalState] = useState(false);
    const [modalStyle] = useState(getModalStyle);
    const [modalData, setModalData] = useState<HoldingData>({} as HoldingData);
    const [loading, setLoading] = useState<number>(0);

    const useStyles = makeStyles((theme) => ({
        modal: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        paper: {
            position: 'absolute',
            backgroundColor: theme.palette.background.paper,
            boxShadow: theme.shadows[5],
            padding: theme.spacing(2, 4, 3),
            'overflow-y': 'scroll',
            'max-height': '800px',
        },
    }));
    const classes = useStyles();

    const Party2Data = (p: Party, parties: Parties) => {
        let id;
        if (!p) {
            return;
        }
        if (
            p.$ &&
            p.Person &&
            ((feed === feedType.pending && carrier === 'North_American') || carrier === 'National_Life')
        ) {
            id = p.$.id;
        } else if (feed === feedType.inforce && p.Person) {
            id = p.id;
        } else {
            return;
        }
        const item: Party = {
            id,
            firstName: p.Person.FirstName,
            lastName: p.Person.LastName,
            gender: p.Person?.Gender?._,
            age: p.Person.Age,
            dob: p.Person.BirthDate,
        };
        parties.set(item.id, item);
    };

    const getHoldingPeople = (p: Policy, peopleList: People): (Person | null | undefined)[] => {
        let participants: (Person | null | undefined)[] = [];
        if (carrier === 'National_Life') {
            // eslint-disable-next-line no-restricted-syntax
            for (const value of peopleList.values()) {
                participants.push(value);
            }
        } else if (feed === feedType.pending) {
            participants =
                p.Life.Coverage && p.Life.Coverage.LifeParticipant
                    ? p.Life.Coverage.LifeParticipant.map((n) => {
                          if (n.$ == null) {
                              return null;
                          }
                          return peopleList.get(n.$.PartyID);
                      })
                    : [];
        } else {
            participants =
                p.Coverage && p.Coverage.LifeParticipant
                    ? p.Coverage.LifeParticipant.map((n) => {
                          const participantData = peopleList.get(n.PartyID);
                          if (participantData) {
                              participantData.role = n.LifeParticipantRoleCode._;
                          }
                          return participantData;
                      })
                    : [];
        }
        return participants;
    };

    const Holding2Data = (h: Holding, people: People, transDate: Date): HoldingData | undefined => {
        if (h) {
            const p = h.Policy;
            return {
                status: p.PolicyStatus?._ || '',
                annualPaymentAmt: p.AnnualPaymentAmt,
                planName: p.PlanName,
                faceAmount: p.Life.FaceAmt,
                carrierCode: p.CarrierCode,
                effDate: p.EffDate,
                paidToDate: p.PaidToDate,
                holding: h,
                requirementInfo:
                    p.RequirementInfo && Array.isArray(p.RequirementInfo)
                        ? p.RequirementInfo.map((req) => ({
                              id: req.$?.id,
                              hoRequirementRefID: req.HORequirementRefID,
                              reqCodeTc: req.ReqCode.$.tc,
                              reqCode: req.ReqCode._,
                              reqStatusTc: req.ReqStatus.$.tc,
                              reqStatus: req.ReqStatus._,
                              requestedDate: req.RequestedDate,
                              requirementDetails: req.RequirementDetails,
                              requirementInfoUniqueID: req.RequirementInfoUniqueID,
                          }))
                        : [],
                people: getHoldingPeople(p, people),
                transDate,
            };
        }
        return undefined;
    };

    const getPolicyData = (OLifE: OLifE | undefined, transDate: Date): (HoldingData | undefined)[] => {
        const policyData = new Array<ReturnType<typeof Holding2Data>>();
        const people: People = new Map<string, Person>();
        const party = OLifE?.Party;
        if (Array.isArray(party)) {
            party.forEach((h) => Party2Data(h, people as unknown as Parties));
        } else {
            Party2Data(party!, people as unknown as Parties);
        }
        const holdings = OLifE?.Holding;
        if (Array.isArray(holdings)) {
            policyData.push(...holdings.map((h) => Holding2Data(h, people, transDate)));
        } else {
            policyData.push(Holding2Data(holdings!, people, transDate));
        }
        return policyData;
    };

    useEffect(() => {
        let fileListUrl = `data-feed/list/${carrier}/${feed}`;
        if (carrier === 'National_Life') {
            fileListUrl = `data-feed/list/${carrier}`;
        }
        type Response = Array<{
            Key: string; // 'North_American/1125-Pending-Business-Production/20210514024916_NorthAmerican_1125.xml'
            LastModified: string; // 2021-05-14T21:04:29.000Z
            ETag: string; // '"f7b048f55b09e7c545ab92bd227487b0"'
            Size: number; // 53583
            StorageClass: string; // 'STANDARD'
        }>;
        API.Call<Response | {error: string}>(fileListUrl).then((files: (Response | {error: string}) | null) => {
            if (!files || !Array.isArray(files)) {
                throw new Error(`Failed to fetch file list: ${files?.error}`);
            }
            const now = new Date().getTime();
            const sevenDays = 1000 * 3600 * 24 * 7;
            const recentFiles = files.filter((x) => {
                const diff = now - new Date(x.LastModified).getTime();
                return diff < sevenDays;
            });
            let filesToProcessed = recentFiles;
            if (carrier === 'National_Life') {
                if (feed === feedType.pending) {
                    filesToProcessed = recentFiles.filter(
                        (x) => x.Key.startsWith('National_Life/Amplify-6SR') && x.Size < 5000000,
                    );
                } else if (feed === feedType.inforce) {
                    filesToProcessed = recentFiles.filter(
                        (x) => x.Key.startsWith('National_Life/6SR_Inforce') && x.Size < 5000000,
                    );
                }
            } else {
                filesToProcessed = recentFiles.filter((x) => x.Size < 5000000);
            }
            Promise.all(
                filesToProcessed.map((processed) =>
                    API.Call<
                        Record<string, {TXLifeRequest: {TransExeDate: string; TransExeTime: string; OLifE: OLifE}}>
                    >(`data-feed/file/${processed.Key}`).then((x) => {
                        setLoading(loading + 1);
                        return x;
                    }),
                ),
            ).then((contents) => {
                setLoading(-1);
                const fileData = new Array<HoldingData | undefined>();
                contents.forEach((c) => {
                    if (!c) {
                        throw new Error('Failed to fetch file contents');
                    }
                    const txLife =
                        feed === feedType.inforce && carrier === 'North_American' ? c['tx_:TXLife'] : c.TXLife;
                    if (txLife && txLife.TXLifeRequest && txLife.TXLifeRequest) {
                        if (Array.isArray(txLife.TXLifeRequest)) {
                            fileData.push(
                                ...txLife.TXLifeRequest.map(
                                    (l: {TransExeDate: string; TransExeTime: string; OLifE: OLifE}) => {
                                        const transTimeStamp = new Date(`${l.TransExeDate} ${l.TransExeTime}`);
                                        return getPolicyData(l.OLifE, transTimeStamp)[0];
                                    },
                                ),
                            );
                        } else {
                            const transDate = txLife.TXLifeRequest.TransExeDate;
                            const transTime = txLife.TXLifeRequest.TransExeTime;
                            const transTimeStamp = new Date(`${transDate} ${transTime}`);
                            fileData.push(...getPolicyData(txLife.TXLifeRequest.OLifE, transTimeStamp));
                        }
                    }
                });
                const sortedData = fileData.sort(
                    (a, b) => new Date(b?.transDate || '').getTime() - new Date(a?.transDate || '').getTime(),
                );
                const uniqueData = sortedData.filter(
                    (x, i, arr) =>
                        arr.findIndex((a) => a?.holding.Policy.PolNumber === x?.holding.Policy.PolNumber) === i,
                );
                setData(uniqueData);
            });
        });
    }, [carrier, feed]);

    const openDetailModal = (ins: any, policy: any) => {
        setModalState(true);
        setModalData(policy);
    };
    const handleClose = () => {
        setModalState(false);
    };
    return (
        <Grid container spacing={3}>
            <Grid item xs={6}>
                <Select
                    value={carrier}
                    style={{width: '100%'}}
                    onChange={(e) => {
                        setCarrier(e.target.value as string);
                    }}
                >
                    <MenuItem value={'North_American'}>North American</MenuItem>
                    <MenuItem value={'National_Life'}>National Life</MenuItem>
                </Select>
            </Grid>
            <Grid item xs={6}>
                <Select
                    style={{width: '100%'}}
                    value={feed}
                    onChange={(e) => {
                        setFeed(e.target.value as string);
                    }}
                >
                    <MenuItem value={feedType.pending}>Pending</MenuItem>
                    <MenuItem value={feedType.inforce}>Inforce</MenuItem>
                </Select>
            </Grid>

            <Grid item xs={12}>
                {loading > -1 && <h4>loading ({loading})... please wait...</h4>}
                <TableContainer component={Paper}>
                    <Table style={{minWidth: '100%'}}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Policy No</TableCell>
                                <TableCell>First Name</TableCell>
                                <TableCell>Last Name</TableCell>
                                <TableCell>Gender</TableCell>
                                <TableCell>Birth Date</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Face Amount</TableCell>
                                <TableCell>Plan Name</TableCell>
                                <TableCell>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data?.map((p) => {
                                const ins = p?.people?.find(
                                    (x: any) =>
                                        x &&
                                        (x.id.startsWith('Party_Primary Insured') ||
                                            x.role == participantRoleType.primaryInsured ||
                                            x.id == 'Insured_1' ||
                                            x.id.startsWith('Insured_Party_')),
                                );
                                if (ins)
                                    return (
                                        <TableRow>
                                            <TableCell>{p?.holding.Policy.PolNumber}</TableCell>
                                            <TableCell>{ins.firstName}</TableCell>
                                            <TableCell>{ins.lastName}</TableCell>
                                            <TableCell>{ins.gender}</TableCell>
                                            <TableCell>{ins.dob}</TableCell>
                                            <TableCell>{p?.status}</TableCell>
                                            <TableCell>${parseFloat(p?.faceAmount || '').toLocaleString()}</TableCell>
                                            <TableCell>{p?.planName}</TableCell>
                                            <TableCell>
                                                <Button
                                                    onClick={() => {
                                                        openDetailModal(ins, p);
                                                    }}
                                                >
                                                    Detail
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Grid>
            {modalData.holding ? (
                <Modal open={modalState} onClose={handleClose}>
                    <div style={modalStyle} className={classes.paper}>
                        <h2>Detail view</h2>
                        <Accordion defaultExpanded={true}>
                            <AccordionSummary expandIcon={<ExpandMore />}>General Info</AccordionSummary>
                            <AccordionDetails>
                                <Table style={{minWidth: '100%'}}>
                                    <TableRow>
                                        <TableCell>Plan name : {modalData.planName}</TableCell>
                                        <TableCell>Pol Number : {modalData.holding.Policy.PolNumber}</TableCell>
                                        <TableCell>Product Code : {modalData.holding.Policy.ProductCode}</TableCell>
                                        <TableCell>Carrier Code : {modalData.carrierCode}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>Face Amount : {modalData.faceAmount}</TableCell>
                                        <TableCell>Annual Payment Amt : {modalData.annualPaymentAmt}</TableCell>
                                        <TableCell>Effective Date : {modalData.effDate}</TableCell>
                                        <TableCell>Paid To Date : {modalData.paidToDate}</TableCell>
                                    </TableRow>
                                </Table>
                            </AccordionDetails>
                        </Accordion>

                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMore />}>Requirements</AccordionSummary>
                            <AccordionDetails>
                                <Table style={{minWidth: '100%'}}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>id</TableCell>
                                            <TableCell>Requirement Ref ID</TableCell>
                                            <TableCell>Request Code</TableCell>
                                            <TableCell>Request Status</TableCell>
                                            <TableCell>Requested Date</TableCell>
                                            <TableCell>Requirement Details</TableCell>
                                            <TableCell>Requirement Info Unique ID</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {modalData?.requirementInfo?.map((r: any) => {
                                            return (
                                                <TableRow>
                                                    <TableCell>{r.id}</TableCell>
                                                    <TableCell>{r.hoRequirementRefID}</TableCell>
                                                    <TableCell>{r.reqCode}</TableCell>
                                                    <TableCell>{r.reqStatus}</TableCell>
                                                    <TableCell>{r.requestedDate}</TableCell>
                                                    <TableCell>{r.requirementDetails}</TableCell>
                                                    <TableCell>{r.RequirementInfoUniqueID}</TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </AccordionDetails>
                        </Accordion>
                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMore />}>JSON view</AccordionSummary>
                            <AccordionDetails>
                                <ReactJson
                                    src={modalData.holding}
                                    collapsed={true}
                                    displayDataTypes={false}
                                    displayObjectSize={false}
                                />
                            </AccordionDetails>
                        </Accordion>
                    </div>
                </Modal>
            ) : (
                ''
            )}
        </Grid>
    );
});
